import { z } from 'zod';

import type { PromiseOfOptional, SyncOrAsync } from '../types';

export enum AuthEvent {
  SignOut = 'SIGN_OUT',
}

export type EventHandler = () => void;

export interface EventSubscription {
  /**
   * Function used to clear the subscription of an event
   */
  remove: () => void;
}

export interface AuthClient<T> {
  /**
   * Retrieves the user information for the currently logged-in user
   */
  getUser(): PromiseOfOptional<T>;
  /**
   * Retrieves the session information for the currently logged-in user
   */
  getSession(): PromiseOfOptional<AuthSession<T>>;
  /**
   * Clears the session for the currently logged-in user
   */
  clearSession(): SyncOrAsync<void>;
  /**
   * Retrieves the credentials for the currently logged-in user
   */
  getCredentials(): PromiseOfOptional<Credentials>;
  /**
   * Initiates the sign out process for the currently logged-in user
   */
  signOut(): Promise<void>;
  /**
   * Refreshes credentials for the currently logged in user
   */
  refreshTokens(): PromiseOfOptional<Credentials>;
  /**
   * Adds handler that listens to the supplied event
   *
   * @param event - The authentication event you want to listen to
   * @param handler - The function responsible for handling the event
   */
  addEventListener(event: AuthEvent, handler: EventHandler): EventSubscription;
}

export const CredentialsSchema = z.object({
  accessToken: z.string(),
  idToken: z.string(),
  refreshToken: z.string(),
});

export type Credentials = z.infer<typeof CredentialsSchema>;

export interface AuthSession<TUser> {
  /**
   * Indicates in the session is valid
   */
  isValid(): SyncOrAsync<boolean>;
  /**
   * Retrieves the access token, does not indicate validity of the token
   */
  getAccessToken(): SyncOrAsync<string>;
  /**
   * Retrieves the id token, does not indicate validity of the token
   */
  getIdToken(): SyncOrAsync<string>;
  /**
   * Retrieves the refresh token, does not indicate validity of the token
   */
  getRefreshToken(): SyncOrAsync<string>;
  /**
   * Retrieve the session user
   */
  getUser(): TUser;
}

export interface SessionClient<T> {
  /**
   * Creates a session for the relevant credentials
   *
   * @param credentials - The credentials of the user
   */
  createSession(credentials: Credentials): AuthSession<T>;
}
